import { Transition } from "@headlessui/react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";

import { checkAndRedirectByRole } from "../utils/authUtils";
import { getUserDetails } from "../utils/getUserDetails";

import { useAlert } from "./AlertProvider";
import Navbar from "./Navbar";

export default function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [touchedFields, setTouchedFields] = useState({
    email: false,
    password: false,
  });
  const { email, password } = formData;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const showAlert = useAlert();

  useEffect(() => {
    const verifySession = async () => {
      const id = Cookies.get("id");
      const role = Cookies.get("role");
      if (id || role) {
        try {
          await getUserDetails();
          checkAndRedirectByRole(navigate);
        } catch (error) {
          Cookies.remove("id");
          Cookies.remove("role");
          Cookies.remove("csrftoken");
        }
      }
    };
    verifySession();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (!touchedFields[e.target.name]) {
      setTouchedFields({ ...touchedFields, [e.target.name]: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTouchedFields({ email: true, password: true });

    if (!email || !password) {
      showAlert("Please fill in all fields", "error");
      setLoading(false);
      return;
    }

    try {
      const csrfToken = Cookies.get("csrftoken");
      const payload = { email, password };
      const loginResponse = await axios.post("/api/login/", payload, {
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (loginResponse.data?.id) {
        const userDetails = await getUserDetails();
        const userId = userDetails?.id;
        Cookies.set("id", userId, { expires: 7 });

        const userResponse = await axios.get(`/api/users/${userId}/`, {
          withCredentials: true,
        });
        const role = userDetails?.role;
        const company = userResponse.data?.company;

        // Check if candidate is new (no applicationids or empty array)
        if (role === "candidate") {
          const isNewUser =
            !userResponse.data.applicationids ||
            userResponse.data.applicationids.length === 0;
          if (isNewUser) {
            navigate("/setprofile", { replace: true });
            return;
          }
        }

        if (role) {
          Cookies.set("role", role, { expires: 7 });
          if (
            (role === "recruiter" ||
              role === "smallbiz_owner" ||
              role === "nonprofit") &&
            !company
          ) {
            navigate("/orgprofile", { replace: true });
          } else {
            // Normal navigation
            let path = "/";
            if (role === "candidate") path = "/home";
            else path = "/orghome";
            navigate(path, { replace: true });
          }
        } else {
          showAlert("User role missing", "error");
        }
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 400) {
        showAlert("Invalid email or password.", "error");
      } else if (status === 404) {
        showAlert("User not found.", "error");
      } else if (status) {
        showAlert("Login Failed", "error");
      } else if (error.request) {
        showAlert("Network issue.", "error");
      } else {
        showAlert("Unexpected error. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  const handleGoogleSuccess = async (response) => {
    try {
      const csrfToken = Cookies.get("csrftoken");
      const payload = { token: response.credential };
      const res = await axios.post("/api/googlelogin/", payload, {
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (res.status === 200 && res.data.user?.id) {
        const userId = res.data.user.id;
        Cookies.set("id", userId, { expires: 7 });

        const userRes = await axios.get(`/api/users/${userId}/`, {
          withCredentials: true,
        });

        const role = userRes.data?.role;
        const company = userRes.data?.company;

        if (role) {
          Cookies.set("role", role, { expires: 7 });

          if (
            (role === "recruiter" ||
              role === "smallbiz_owner" ||
              role === "nonprofit") &&
            !company
          ) {
            navigate("/orgprofile", { replace: true });
          } else {
            // Normal navigation
            let path = "/";
            if (role === "candidate") {
              path = "/setprofile";
              navigate(path, { replace: true, state: { from: "login" } });
            } else path = "/orghome";
            navigate(path, { replace: true });
          }
        } else {
          showAlert("User role missing", "error");
        }
      } else {
        showAlert("Failed to sign up with Google", "error");
      }
    } catch (error) {
      showAlert("Google signup failed", "error");
    }
  };

  const handleGoogleFailure = () => showAlert("Google login failed", "error");

  return (
    <GoogleOAuthProvider clientId="637231094750-2iviubrnmb70qmt9g89nr1nb1u6cqd43.apps.googleusercontent.com">
      <Navbar />
      <div className="bg-white dark:bg-gray-900 flex justify-center">
        <Transition
          appear
          enter="transition-all duration-700 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          show
        >
          <div className="p-4 flex flex-col items-center justify-center w-full max-w-4xl">
            <h3 className="m-8 text-2xl font-bold text-gray-900 dark:text-gray-100 text-center">
              Welcome Back!
            </h3>
            <div className="flex flex-col p-6 md:p-10 w-full md:w-3/5 shadow-md rounded-md mb-8">
              {/* Form */}
              <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-y-2">
                  <label
                    className="text-sm font-medium text-gray-900 dark:text-gray-100"
                    htmlFor="email"
                  >
                    Email address
                  </label>
                  <input
                    className={`w-full h-10 p-2 rounded-lg border-2 ${touchedFields.email && !email ? "border-red-500" : "border-gray-200 dark:border-gray-600"} focus:outline-none focus:border-blue-500 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                    name="email"
                    required
                    type="email"
                    value={email}
                    onChange={handleChange}
                  />
                </div>
                <div className="relative flex flex-col gap-y-2">
                  <label
                    className="text-sm font-medium text-gray-900 dark:text-gray-100"
                    htmlFor="password"
                  >
                    Password
                  </label>
                  <input
                    className={`w-full px-4 py-2 rounded-lg border-2 ${touchedFields.password && !password ? "border-red-500" : "border-gray-200 dark:border-gray-600"} focus:outline-none focus:border-blue-500 transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100`}
                    name="password"
                    required
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handleChange}
                  />
                  <button
                    className="absolute right-3 top-12 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200"
                    type="button"
                    onClick={toggleShowPassword}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>

                <div className="flex justify-between">
                  <div className="flex flex-row items-center gap-x-4">
                    <input
                      className="w-4 h-4 border border-gray-300 dark:border-gray-600 focus:outline-none accent-blue-500"
                      id="remember"
                      name="remember"
                      type="checkbox"
                    />
                    <label
                      className="text-sm font-light text-gray-900 dark:text-gray-100"
                      htmlFor="remember"
                    >
                      Remember Me
                    </label>
                  </div>
                  <Link
                    className="text-sm font-semibold text-blue-700 dark:text-blue-400"
                    to="/login"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  className="w-full h-[40px] cursor-pointer bg-blue-700 text-white font-medium rounded-lg shadow-md hover:bg-blue-600 disabled:bg-blue-300 transition-all duration-300 transform focus:outline-none focus:ring focus:ring-blue-400"
                  disabled={loading}
                  type="submit"
                >
                  {loading ? "Logging In..." : "Log In"}
                </button>
              </form>

              <div className="m-6 relative flex items-center justify-center">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <span className="relative bg-white dark:bg-gray-900 px-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                  or continue with
                </span>
              </div>

              {/* Google Login */}
              <div className="m-2 flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  text="signin_with"
                  // shape="pill"
                  width="300"
                  onError={handleGoogleFailure}
                  // theme="filled_blue"
                  size="large"
                />
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              Don’t have an account?{" "}
              <Link
                className="text-blue-500 dark:text-blue-400 font-semibold hover:text-blue-600 dark:hover:text-blue-300 transition-colors duration-200"
                to="/landingpage"
              >
                Sign Up
              </Link>
            </p>
          </div>
        </Transition>
      </div>
    </GoogleOAuthProvider>
  );
}

//
